import FeatureGates from '@atlaskit/feature-gate-js-client';
import { DEFAULT_INTL_CONFIG, IntlErrorCode, type OnErrorFn } from '@formatjs/intl';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { type PropsWithChildren } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
// oxlint-disable-next-line no-restricted-imports -- Temporarily required while this provider supports both react-intl package names
import {
	IntlProvider as IntlProviderNext,
	type MessageFormatElement,
	useIntl,
} from 'react-intl-next';

import { type JSX } from 'react/jsx-runtime';
import { type MessageLoader, useIntlMessages } from './useIntlMessages';

export type IntlProviderProps<T> = {
	messages?: T;
	messagesLoader: MessageLoader<T>;
};

const DOUBLE_WRAP_INTL_PROVIDER_GATE = 'post-office_enable_double-wrap-intl-provider';
const SUPPRESS_MISSING_TRANSLATION_ERRORS_GATE = 'post_office_suppress_missing_translation_errors';

function useFetchLocale() {
	let locale = 'en';
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	try {
		const { locale: intlLocale } = useIntl();
		locale = intlLocale;
	} catch (err) {
		fireAnalyticsEvent({
			action: 'errored',
			actionSubject: 'intlProvider',
			eventType: 'operational',
			attributes: {
				errorMessage: err,
			},
		});
	}

	return { locale };
}

export const handleError: OnErrorFn = (err) => {
	// Do not spam local devloop with missing translation errors
	//
	// See: https://github.com/formatjs/formatjs/issues/465#issuecomment-643887472
	if (err?.code === IntlErrorCode.MISSING_TRANSLATION) {
		return;
	}

	return DEFAULT_INTL_CONFIG.onError(err);
};

export function IntlProvider<
	T extends Record<string, string> | Record<string, MessageFormatElement[]>,
>(props: PropsWithChildren<IntlProviderProps<T>>): JSX.Element {
	const { locale } = useFetchLocale();
	const messages = useIntlMessages(locale, props.messagesLoader, props.messages);
	const shouldDoubleWrapIntlProvider = FeatureGates.checkGate(DOUBLE_WRAP_INTL_PROVIDER_GATE);
	const shouldSuppressMissingTranslationErrors = FeatureGates.checkGate(
		SUPPRESS_MISSING_TRANSLATION_ERRORS_GATE,
	);

	const suppressMissingTranslationErrorsProps = shouldSuppressMissingTranslationErrors
		? { onError: handleError }
		: {};

	if (!shouldDoubleWrapIntlProvider) {
		return (
			<IntlProviderNext
				locale={locale}
				messages={messages}
				{...suppressMissingTranslationErrorsProps}
			>
				{props.children}
			</IntlProviderNext>
		);
	}

	return (
		<ReactIntlProvider
			locale={locale}
			messages={messages}
			{...suppressMissingTranslationErrorsProps}
		>
			<IntlProviderNext
				locale={locale}
				messages={messages}
				{...suppressMissingTranslationErrorsProps}
			>
				{props.children}
			</IntlProviderNext>
		</ReactIntlProvider>
	);
}
